// Static bootstrap for the institution this app instance serves. Imported by
// BOTH the backend (which uses the URLs to reach the Pod) and the browser
// (which uses the labels to render). Contains no secrets — the institutional
// client credentials live only in the backend's environment.
//
// Partners are NOT here: the partner registry lives in the Pod
// (config/solid-data-app/partners.json) and reaches the browser via /api/*.
// Bootstrap is only the CSS URL, this institution, namespaces, and roles.

const env = globalThis.process?.env ?? {};
export const CSS_URL = env.CSS_PUBLIC_URL ?? env.CSS_URL ?? 'http://localhost:3000';

// The institution this app instance acts as. One institution, one institutional
// WebID — the identity every partner grants access to. Staff are told apart by
// role (see ROLES), which is an org-internal concern, not a Solid identity.
//
// This is only what the app needs to *reach* the Pod (slug, WebID, containers).
// The institution's presentation identity — name, label, tagline, brand colour —
// is Pod-resident (config/solid-data-app/institution.json), reaching the browser
// via GET /api/context and the backend via readInstitution(). Same identity, one
// source, and it lives in the Pod (Phase C5).
const institution = env.SOLIDSHARE_ACTIVE_INSTITUTION ?? '';
const podName = env.SOLIDSHARE_ACTIVE_POD ?? institution;
const podBase = podName ? `${CSS_URL}/${podName}/` : '';

// On the server these values come from the ignored runtime environment. In the
// browser the object starts empty and applyContext() fills it from /api/context;
// credentials never enter this shared module or the frontend bundle.
export const SELF = {
  institution,
  webId: podBase ? `${podBase}profile/card#me` : '',
  podBase,
  records: podBase ? `${podBase}diversity/` : '',
  aggregates: podBase ? `${podBase}diversity/aggregates/` : '',
};

export function configureSelfFromEnv(source = globalThis.process?.env ?? {}) {
  const active = source.SOLIDSHARE_ACTIVE_INSTITUTION ?? '';
  const activePod = source.SOLIDSHARE_ACTIVE_POD ?? active;
  const base = activePod ? `${source.CSS_PUBLIC_URL ?? source.CSS_URL ?? CSS_URL}/${activePod}/` : '';
  Object.assign(SELF, {
    institution: active,
    webId: base ? `${base}profile/card#me` : '',
    podBase: base,
    records: base ? `${base}diversity/` : '',
    aggregates: base ? `${base}diversity/aggregates/` : '',
  });
  return SELF;
}

// The three roles from roles.md. Which screens a person sees, and whether they
// may publish, follows from their role — not from a Solid session. In a real
// deployment these map to the institution's own directory (SSO/LDAP); here the
// picker stands in for that.
export const ROLES = {
  technical: {
    id: 'technical',
    label: 'Technical owner',
    blurb: 'Wire up the source, run it regularly, and keep track of how far it has read.',
    screens: ['connector', 'connector/run'],
  },
  governance: {
    id: 'governance',
    label: 'Governance owner',
    blurb: 'Decide who may read what, and send partners an up-to-date copy — you own who sees which cubes.',
    screens: ['partners', 'partner', 'partner/access', 'partner/confirm', 'catalog'],
  },
  analyst: {
    id: 'analyst',
    label: 'Analyst / viewer',
    blurb: 'Assess and label source data, build private cubes, browse records, and write the questions you want answered.',
    screens: ['disclosure', 'disclosure/assess', 'disclosure/label', 'disclosure/aggregates', 'disclosure/confirm', 'records', 'catalog', 'queries', 'analysis'],
  },
};

// Publishing writes WAC rules and is the one irreversible act in the app, so it
// is gated on the governance role — enforced in the backend, not just hidden in
// the UI.
export const PUBLISH_ROLE = 'governance';

// Ingest writes private ADR records and recomputes the private cube — a
// technical-owner act, gated in the backend the same way.
export const INGEST_ROLE = 'technical';

// Named analysis queries are authored by the analyst; saving/deleting one
// writes queries.json in the Pod, gated in the backend the same way.
export const QUERY_AUTHOR_ROLE = 'analyst';
export const DISCLOSURE_AUTHOR_ROLE = 'analyst';
export const AGGREGATE_AUTHOR_ROLE = 'analyst';

// Namespaces used across queries and rendering.
export const NS = {
  adr: 'https://data.culturalheritage.network/ontology/adr#',
  schema: 'https://schema.org/',
  dcterms: 'http://purl.org/dc/terms/',
  qb: 'http://purl.org/linked-data/cube#',
  void: 'http://rdfs.org/ns/void#',
  ldp: 'http://www.w3.org/ns/ldp#',
};
